import React, { createContext, useState, useCallback } from 'react';

export interface ModalAction {
  content?: React.ReactNode;
  onAction?: (...args: unknown[]) => void;
  destructive?: boolean;
  loading?: boolean;
  disabled?: boolean;
  [extra: string]: unknown;
}

export interface ModalState {
  title: React.ReactNode;
  content?: React.ReactNode;
  primaryAction: ModalAction | null;
  secondaryAction: ModalAction | null;
  tertiaryAction: ModalAction | null;
  isError: boolean;
  loading: boolean;
  visible: boolean;
  accept: unknown;
  type: string | null;
  chromeless: boolean;
  maxWidth: string;
  /** show() spreads caller args verbatim; tolerate extras without widening reads. */
  [extra: string]: unknown;
}

export interface ModalContextValue {
  show: (args?: Partial<ModalState>) => void;
  hide: () => void;
  loading: (val: boolean) => void;
  modal: ModalState;
}

const initialModal: ModalState = {
  title: '',
  content: '',
  primaryAction: null,
  secondaryAction: {
    content: 'Cancel',
  },
  tertiaryAction: null,
  isError: false,
  loading: false,
  visible: false,
  accept: null,
  type: null,
  chromeless: false,
  maxWidth: '500px',
};

// Default is a cast, not null: every consumer lives under the provider, and the
// ~30 existing `useContext` destructures must keep compiling unchanged (B1).
export const ModalContext = createContext<ModalContextValue>({} as ModalContextValue);

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modal, setModal] = useState<ModalState>(initialModal);

  const show = useCallback(
    (args?: Partial<ModalState>) => {
      setModal({ ...initialModal, visible: true, ...args });
    },
    [modal],
  );

  const loading = useCallback((val: boolean) => {
    setModal((prevState) => ({
      ...prevState,
      loading: val,
    }));
  }, []);

  const hide = useCallback(() => {
    // Reset fully so a later standalone mount never inherits a previous show()'s
    // accept/type/primaryAction/title. show() rebuilds from initialModal anyway.
    setModal(initialModal);
  }, []);

  return (
    <ModalContext.Provider
      value={{
        hide,
        show,
        loading,
        modal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};
